import React, { useState, useEffect } from 'react';
import { Mail, AlertCircle, Loader2, CheckCircle, ArrowLeft, KeyRound } from 'lucide-react';
import { api } from '../../api/client';
import './Auth.css';

interface ForgotPasswordModalProps {
    onBack: () => void;
    onOAuthRecovery?: () => void;
}

export default function ForgotPasswordModal({ onBack, onOAuthRecovery }: ForgotPasswordModalProps) {
    const [email, setEmail] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasOAuth, setHasOAuth] = useState(false);

    useEffect(() => {
        api.getOAuthConfig().then((cfg) => {
            setHasOAuth(!!(cfg.google_client_id || cfg.microsoft_client_id));
        }).catch(() => { });
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            await api.forgotPassword(email);
            setIsSubmitted(true);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to send reset email');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-card">
                <div className="auth-header">
                    <div className="auth-logo">TC</div>
                    <h1>Reset Password</h1>
                    <p>{isSubmitted ? 'Check your email' : 'Enter your email to receive a reset link'}</p>
                </div>

                {isSubmitted ? (
                    <div className="auth-form">
                        <div className="auth-success">
                            <CheckCircle size={16} />
                            <span>If an account exists for <strong>{email}</strong>, you will receive reset instructions.</span>
                        </div>
                        <button className="auth-submit" onClick={onBack}>
                            <ArrowLeft size={18} />
                            Return to Login
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="auth-form">
                        {error && (
                            <div className="auth-error">
                                <AlertCircle size={16} />
                                <span>{error}</span>
                            </div>
                        )}

                        <div className="auth-field">
                            <label htmlFor="email">Email Address</label>
                            <div className="auth-input-wrapper">
                                <Mail size={18} />
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        <button type="submit" className="auth-submit" disabled={isLoading}>
                            {isLoading ? (
                                <>
                                    <Loader2 size={18} className="spin" />
                                    Sending…
                                </>
                            ) : (
                                <>
                                    <Mail size={18} />
                                    Send Reset Link
                                </>
                            )}
                        </button>

                        {hasOAuth && onOAuthRecovery && (
                            <>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    color: '#6b7280',
                                    fontSize: 12
                                }}>
                                    <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
                                    or
                                    <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
                                </div>

                                <button
                                    type="button"
                                    className="auth-oauth-btn"
                                    onClick={onOAuthRecovery}
                                >
                                    <KeyRound size={18} style={{ color: '#4A9EFF' }} />
                                    Recover with linked Google / Microsoft account
                                </button>
                            </>
                        )}

                        <button type="button" className="auth-link" onClick={onBack}>
                            <ArrowLeft size={14} style={{ marginRight: 4 }} />
                            Back to login
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
